import {
  DEFAULT_REGIONAL_FORMAT,
  type FormatNumberOptions,
  formatDate as formatDateShared,
  formatDateTime as formatDateTimeShared,
  formatNumber as formatNumberShared,
  parseNumber as parseNumberShared,
  type RegionalFormatSettings,
} from "@repo/shared";
import { createContext, type ReactNode, useContext, useMemo } from "react";

/**
 * Tenant regional format settings (QL-108), fed from app-bootstrap on the
 * advisor app and from the public tenant endpoint on the public flow. The
 * stored tenant value is the ONLY input — never detected from language or
 * browser. Display-only: values sent to the API stay ISO 8601 / raw numbers.
 */
export type RegionalFormatContextValue = {
  regionalFormat: RegionalFormatSettings;
  /** Bound shared-formatter helpers using the tenant's settings. */
  formatDate: (value: Date | string | null | undefined) => string;
  formatDateTime: (value: Date | string | null | undefined) => string;
  formatNumber: (value: number | null | undefined, options?: FormatNumberOptions) => string;
  parseNumber: (input: string | null | undefined) => number | null;
};

const RegionalFormatContext = createContext<RegionalFormatContextValue | null>(null);

function buildContextValue(settings: RegionalFormatSettings): RegionalFormatContextValue {
  return {
    regionalFormat: settings,
    formatDate: (value) => formatDateShared(value, settings),
    formatDateTime: (value) => formatDateTimeShared(value, settings),
    formatNumber: (value, options) => formatNumberShared(value, settings, options),
    parseNumber: (input) => parseNumberShared(input, settings),
  };
}

type RegionalFormatProviderProps = {
  regionalFormat: RegionalFormatSettings | null | undefined;
  children: ReactNode;
};

export function RegionalFormatProvider({ regionalFormat, children }: RegionalFormatProviderProps) {
  const value = useMemo(
    () => buildContextValue(regionalFormat ?? DEFAULT_REGIONAL_FORMAT),
    [regionalFormat],
  );
  return <RegionalFormatContext.Provider value={value}>{children}</RegionalFormatContext.Provider>;
}

export function useRegionalFormat(): RegionalFormatContextValue {
  const context = useContext(RegionalFormatContext);
  if (!context) {
    // Fallback for components not wrapped in provider
    return buildContextValue(DEFAULT_REGIONAL_FORMAT);
  }
  return context;
}
